import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Linkedin, Twitter, Youtube, Instagram } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Logo } from "@/components/Shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOCIALS = [
  { key: "linkedin", Icon: Linkedin, testid: "footer-social-linkedin" },
  { key: "twitter", Icon: Twitter, testid: "footer-social-twitter" },
  { key: "youtube", Icon: Youtube, testid: "footer-social-youtube" },
  { key: "instagram", Icon: Instagram, testid: "footer-social-instagram" },
];

const Footer = () => {
  const { t } = useLanguage();
  const [social, setSocial] = useState({});

  useEffect(() => {
    axios
      .get(`${API}/settings/public`)
      .then((res) => setSocial(res.data.social || {}))
      .catch(() => {});
  }, []);

  const columns = [
    {
      title: t.footer.product,
      links: [
        { label: t.nav.solution, to: "/solution", testid: "footer-solution-link" },
        { label: t.nav.features, to: "/fonctionnalites", testid: "footer-features-link" },
        { label: t.nav.pricing, to: "/tarifs", testid: "footer-pricing-link" },
      ],
    },
    {
      title: t.footer.company,
      links: [
        { label: t.nav.demo, to: "/demo", testid: "footer-demo-link" },
        { label: t.footer.contact, to: "/demo", testid: "footer-contact-link" },
        { label: t.nav.resources, to: "/ressources", testid: "footer-resources-link" },
      ],
    },
    {
      title: t.footer.legal,
      links: [
        { label: t.footer.legalNotice, to: "/mentions-legales", testid: "footer-legal-notice-link" },
        { label: t.footer.terms, to: "/cgu", testid: "footer-terms-link" },
        { label: t.footer.privacy, to: "/confidentialite", testid: "footer-privacy-link" },
        { label: "Back office", to: "/admin/login", testid: "footer-backoffice-link" },
      ],
    },
  ];

  const activeSocials = SOCIALS.filter((s) => social[s.key]);

  return (
    <footer data-testid="footer" className="bg-[#0A0F1D] text-slate-300">
      <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div>
            <Logo dark />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-slate-400">{t.footer.baseline}</p>
            {activeSocials.length > 0 && (
              <div className="mt-6 flex items-center gap-3" data-testid="footer-socials">
                {activeSocials.map(({ key, Icon, testid }) => (
                  <a
                    key={key}
                    href={social[key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={testid}
                    aria-label={key}
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-slate-400 transition-all duration-300 hover:border-emerald-400/50 hover:text-emerald-400"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            )}
            <div className="mt-6 flex items-center gap-2 text-xs text-slate-400" data-testid="footer-status">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
              </span>
              {t.footer.status}
            </div>
          </div>
          {columns.map((col) => (
            <div key={col.title}>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{col.title}</p>
              <ul className="mt-4 space-y-3">
                {col.links.map((l) => (
                  <li key={l.testid}>
                    <Link
                      to={l.to}
                      data-testid={l.testid}
                      className="text-sm text-slate-400 transition-colors duration-200 hover:text-white"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-6 sm:flex-row sm:items-center">
          <p className="text-xs text-slate-500" data-testid="footer-copyright">
            {t.footer.copyright}
          </p>
          <p className="font-mono-orbit text-[10px] uppercase tracking-[0.25em] text-slate-600">
            Operational reporting & BI tracker
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
